import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { COMMON_TRADES } from "@pph-safety/shared";
import { authClient } from "@/lib/auth-client";

const API_BASE = import.meta.env.VITE_API_URL || "";

/**
 * Subcontractor sign-up page, opened from a company's QR code or link (public).
 * A new person signs up and waits for approval. Someone who already has an account
 * signs in here and the project is added to their existing profile.
 */
export const Route = createFileRoute("/sub-join")({
  component: SubJoinPage,
});

interface LinkInfo {
  companyName: string;
  projectName: string;
  divisionName: string;
  orgName: string;
  expiresAt: string;
}

type Mode = "register" | "signin" | "done";

const inputClass =
  "w-full h-11 rounded-md border border-border bg-surface-raised px-3 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-ring";

function SubJoinPage() {
  const [token, setToken] = useState<string | null>(null);
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("register");
  const [doneMessage, setDoneMessage] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [trade, setTrade] = useState("");
  const [customTrade, setCustomTrade] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const match = window.location.hash.match(/token=([^&]+)/);
    if (!match) {
      setProblem("This link is not valid. Please scan the QR code or open the link you were given.");
      setLoading(false);
      return;
    }
    setToken(match[1]);
  }, []);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/sub-join/info/${token}`);
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          setProblem((body as { error?: string }).error || "This link is not valid.");
          return;
        }
        setInfo((body as { data: LinkInfo }).data);
      } catch {
        setProblem("Could not reach the server. Check your connection and try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  async function register(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const finalTrade = trade === "Other" ? customTrade.trim() : trade;
    if (password.length < 8) return setError("Use at least 8 characters for your password.");
    if (password !== confirm) return setError("The two passwords do not match.");
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/sub-join/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, name, email, phone, password, trade: finalTrade || undefined }),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
      if (res.status === 409 && body.code === "existing_account") {
        setPassword("");
        setConfirm("");
        setMode("signin");
        setError(null);
        return;
      }
      if (!res.ok) return setError(body.error || "Could not sign you up. Try again.");
      setDoneMessage("You're signed up. An admin at Paris Mechanical will approve your account, and you'll get a message when it is ready.");
      setMode("done");
    } catch {
      setError("Could not reach the server. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function signInAndJoin(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await authClient.signIn.email({ email: email.trim().toLowerCase(), password });
      if (result.error) return setError(result.error.message || "Invalid email or password.");
      const res = await fetch(`${API_BASE}/api/subs/join`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string; status?: "joined" | "pending" };
      if (!res.ok) return setError(body.error || "Could not add this project to your account.");
      setDoneMessage(
        body.status === "joined"
          ? "This project is now on your account. Open the app to see it."
          : "Your request was sent. An admin must approve the new company before you can use this project."
      );
      setMode("done");
    } catch {
      setError("Could not reach the server. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-bg min-h-screen flex flex-col items-center justify-center px-6 py-12">
      <img src="/paris-safety-logo.png" alt="Paris Mechanical" className="w-48 mb-3 object-contain" />
      <p className="text-text-tertiary text-sm tracking-wider mb-8">Subcontractor sign-up</p>

      <div className="glass-card rounded-xl shadow-2xl w-full max-w-sm p-8">
        {loading && <p className="text-center text-sm text-text-secondary">Loading...</p>}

        {!loading && problem && (
          <div className="space-y-3 text-center">
            <h1 className="text-lg font-semibold text-text-primary">Link not available</h1>
            <p className="text-sm text-text-secondary">{problem}</p>
          </div>
        )}

        {!loading && !problem && info && mode === "done" && (
          <div className="space-y-4 text-center">
            <h1 className="text-lg font-semibold text-text-primary">Thank you</h1>
            <p className="text-sm text-text-secondary">{doneMessage}</p>
            <a href="/login" className="inline-block text-sm text-paris-red-text underline underline-offset-2">Go to the sign-in page</a>
          </div>
        )}

        {!loading && !problem && info && mode !== "done" && (
          <>
            <div className="mb-5 space-y-1 text-center">
              <h1 className="text-lg font-semibold text-text-primary">{info.companyName}</h1>
              <p className="text-sm text-text-secondary">Project: {info.projectName}</p>
            </div>

            {mode === "register" ? (
              <form onSubmit={register} className="space-y-3">
                <input aria-label="Full name" placeholder="Full name" className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required />
                <input aria-label="Email" type="email" placeholder="Email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} required />
                <input aria-label="Phone" type="tel" placeholder="Phone number" className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} required />
                <select aria-label="Trade" className={inputClass} value={trade} onChange={(e) => setTrade(e.target.value)}>
                  <option value="">Your trade (optional)</option>
                  {COMMON_TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
                  <option value="Other">Other</option>
                </select>
                {trade === "Other" && (
                  <input aria-label="Your trade" placeholder="Your trade" className={inputClass} value={customTrade} onChange={(e) => setCustomTrade(e.target.value)} />
                )}
                <input aria-label="Password" type="password" placeholder="Password (8+ characters)" className={inputClass} value={password} onChange={(e) => setPassword(e.target.value)} required />
                <input aria-label="Confirm password" type="password" placeholder="Confirm password" className={inputClass} value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
                {error && <p className="text-sm text-destructive">{error}</p>}
                <button type="submit" disabled={submitting} className="w-full h-11 rounded-md bg-paris-red text-white text-sm font-semibold disabled:opacity-60">
                  {submitting ? "Signing up..." : "Sign up"}
                </button>
                <button type="button" onClick={() => { setMode("signin"); setError(null); }} className="w-full text-center text-sm text-text-secondary underline underline-offset-2">
                  I already have an account
                </button>
              </form>
            ) : (
              <form onSubmit={signInAndJoin} className="space-y-3">
                <p className="text-sm text-text-secondary">
                  Sign in with your existing account. This project will be added to it. No second profile is made.
                </p>
                <input aria-label="Email" type="email" placeholder="Email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} required />
                <input aria-label="Password" type="password" placeholder="Password" className={inputClass} value={password} onChange={(e) => setPassword(e.target.value)} required />
                {error && <p className="text-sm text-destructive">{error}</p>}
                <button type="submit" disabled={submitting} className="w-full h-11 rounded-md bg-paris-red text-white text-sm font-semibold disabled:opacity-60">
                  {submitting ? "Signing in..." : "Sign in and add this project"}
                </button>
                <button type="button" onClick={() => { setMode("register"); setError(null); }} className="w-full text-center text-sm text-text-secondary underline underline-offset-2">
                  I'm new: create an account
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
